import { FormikProps } from "formik";
import React, { FunctionComponent, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { ReactComponent as UploadIcon } from "@src/modules/shared/components/FileInput/icons/upload.svg";
import {
  FileInputHeader,
  FileInputWrapper,
  LoaderWrapper,
} from "@src/modules/shared/components/FileInput/FileInput.components";
import Button from "@src/modules/shared/components/Button";
import { FileLoader } from "@src/modules/shared/components/FileLoader";
import { EditCustomResultsForm } from "@src/modules/shared/components/Results/CustomResults";
import useCustomTestFileHandlers from "@src/modules/shared/components/Results/ResultsFormHeader/hooks/useCustomTestFileHandlers";

interface Props extends FormikProps<EditCustomResultsForm> {
  name?: string;
  accept?: string;
  fileHandler?: (file: File) => Promise<boolean>;
  handleImagesUploaded: () => void;
}

const CustomTestFileInput: FunctionComponent<Props> = ({
  name,
  accept,
  handleImagesUploaded,
  values,
}) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { fileHandler } = useCustomTestFileHandlers();

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    setIsLoading(true);
    if (e.target.files) {
      const files = Array.from(e.target.files);
      if (fileHandler) {
        let success = true;
        for (const file of files) {
          if (!(await fileHandler(file))) success = false;
        }
        if (success) handleImagesUploaded();
      }
      e.target.value = "";
    }

    setIsLoading(false);
  };

  const handleButtonClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  return (
    <FileInputWrapper>
      <FileInputHeader>
        <Button
          size="small"
          buttonType="secondary"
          onClick={handleButtonClick}
          disabled={isLoading}
        >
          <UploadIcon />
          <input type="hidden" name="studyId" value={values.studyId} />
          <input type="hidden" name="testId" value={values.testId} />
          <input
            name={name}
            type="file"
            multiple={true}
            accept={accept}
            ref={fileInputRef}
            onChange={handleUpload}
            style={{ display: "none" }}
          />
          {isLoading ? (
            <LoaderWrapper>
              {t("Uploading")}
              <FileLoader size="small" />
            </LoaderWrapper>
          ) : (
            t("Upload images")
          )}
        </Button>
      </FileInputHeader>
    </FileInputWrapper>
  );
};

export default CustomTestFileInput;
